/**
 * 自动曝光：亮度降采样链 → Pattanaik 自适应 → 1x1 曝光纹理。
 * 全程在 GPU 上完成，避免 CPU 读回。
 */

import * as THREE from 'three'
import {
  fullscreenVertexShader,
} from '../atmosphere/shaders/fullscreen.glsl'
import {
  logLuminanceShader,
  downsampleShader,
  adaptationShader,
} from './shaders/exposure.glsl'

function makeRT(w: number, h: number): THREE.WebGLRenderTarget {
  return new THREE.WebGLRenderTarget(Math.max(1, w), Math.max(1, h), {
    type: THREE.HalfFloatType,
    format: THREE.RGBAFormat,
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    depthBuffer: false,
  })
}

export class AutoExposure {
  private renderer: THREE.WebGLRenderer
  private scene = new THREE.Scene()
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)

  private chain: THREE.WebGLRenderTarget[] = []
  private adaptRT: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget]
  private adaptIndex = 0

  private quad: THREE.Mesh
  private logMat: THREE.ShaderMaterial
  private avgMat: THREE.ShaderMaterial
  private adaptMat: THREE.ShaderMaterial

  /** 曝光值默认初始化为 1（对数亮度 0） */
  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer

    const geometry = new THREE.PlaneGeometry(2, 2)
    this.logMat = new THREE.ShaderMaterial({
      vertexShader: fullscreenVertexShader,
      fragmentShader: logLuminanceShader,
      uniforms: {
        uTex: { value: null },
      },
      depthTest: false,
      depthWrite: false,
    })
    this.avgMat = new THREE.ShaderMaterial({
      vertexShader: fullscreenVertexShader,
      fragmentShader: downsampleShader,
      uniforms: {
        uTex: { value: null },
        uPrevSize: { value: new THREE.Vector2() },
      },
      depthTest: false,
      depthWrite: false,
    })
    this.adaptMat = new THREE.ShaderMaterial({
      vertexShader: fullscreenVertexShader,
      fragmentShader: adaptationShader,
      uniforms: {
        uAvgLogLum: { value: null },
        uPrev: { value: null },
        uDeltaT: { value: 0 },
        uAdaptSpeed: { value: 1 },
      },
      depthTest: false,
      depthWrite: false,
    })
    this.quad = new THREE.Mesh(geometry, this.logMat)
    this.quad.frustumCulled = false
    this.scene.add(this.quad)

    this.adaptRT = [makeRT(1, 1), makeRT(1, 1)]
    // 初始化为曝光 1（对数亮度 0）
    this.adaptRT[0].texture.needsUpdate = true
    this.adaptRT[1].texture.needsUpdate = true
  }

  /** 依据绘制缓冲尺寸重建降采样链 */
  resize(width: number, height: number): void {
    for (const rt of this.chain) rt.dispose()
    this.chain = []
    let w = Math.max(1, Math.floor(width / 2))
    let h = Math.max(1, Math.floor(height / 2))
    while (w >= 1 && h >= 1) {
      this.chain.push(makeRT(w, h))
      if (w === 1 && h === 1) break
      w = Math.max(1, Math.floor(w / 2))
      h = Math.max(1, Math.floor(h / 2))
    }
  }

  /** 当前自适应曝光的 1x1 纹理（g 通道为 exposure） */
  get exposureTexture(): THREE.Texture {
    return this.adaptRT[this.adaptIndex].texture
  }

  /** 最终平均对数亮度纹理（1x1） */
  get averageLogLuminanceTexture(): THREE.Texture {
    return this.chain[this.chain.length - 1].texture
  }

  /** 逐帧更新：scene(全分辨率 HDR) → 链 → 自适应 */
  update(dt: number, adaptSpeed: number, sceneTex: THREE.Texture): void {
    // 第一级：对数亮度
    this.quad.material = this.logMat
    this.logMat.uniforms.uTex.value = sceneTex
    this.renderPass(this.chain[0])

    // 降采样链
    for (let i = 1; i < this.chain.length; i++) {
      this.quad.material = this.avgMat
      this.avgMat.uniforms.uTex.value = this.chain[i - 1].texture
      this.avgMat.uniforms.uPrevSize.value.set(
        this.chain[i - 1].width,
        this.chain[i - 1].height,
      )
      this.renderPass(this.chain[i])
    }

    // 自适应曝光（ping-pong）
    const prev = this.adaptRT[1 - this.adaptIndex]
    const next = this.adaptRT[this.adaptIndex]
    this.quad.material = this.adaptMat
    this.adaptMat.uniforms.uAvgLogLum.value = this.averageLogLuminanceTexture
    this.adaptMat.uniforms.uPrev.value = prev.texture
    this.adaptMat.uniforms.uDeltaT.value = dt
    this.adaptMat.uniforms.uAdaptSpeed.value = adaptSpeed
    this.renderPass(next)
    this.adaptIndex = 1 - this.adaptIndex
  }

  private renderPass(target: THREE.WebGLRenderTarget): void {
    const prev = this.renderer.getRenderTarget()
    this.renderer.setRenderTarget(target)
    this.renderer.render(this.scene, this.camera)
    this.renderer.setRenderTarget(prev)
  }

  dispose(): void {
    for (const rt of [...this.chain, this.adaptRT[0], this.adaptRT[1]]) {
      rt.dispose()
    }
    this.quad.geometry.dispose()
    this.logMat.dispose()
    this.avgMat.dispose()
    this.adaptMat.dispose()
  }
}
